import { defineComponent as _defineComponent } from 'vue';
import { createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, createStaticVNode as _createStaticVNode } from "vue";
const _hoisted_1 = { class: "single-time-slider" };
const _hoisted_2 = { class: "time-labels" };
const _hoisted_3 = {
    key: 0,
    class: "debug-info"
};
const _hoisted_4 = { class: "text-xs text-gray-500 mt-2" };
const _hoisted_5 = { class: "help-text" };
const _hoisted_6 = { class: "text-sm text-gray-600 text-center" };
import { computed } from 'vue';
import SingleRangeSlider from './SingleRangeSlider.vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'SingleTimeSlider',
    props: {
        selectedTime: {},
        operatingHours: { default: () => [] },
        orderType: {},
        constrainToOperating: { type: Boolean, default: true },
        disabled: { type: Boolean, default: false }
    },
    emits: ["update:selectedTime"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        // Debug mode
        const showDebug = computed(() => false); // Disabled for production
        // ✅ TIME CONVERSION HELPERS
        const timeToMinutes = (timeStr) => {
            const [hours, minutes] = timeStr.split(':').map(Number);
            return hours * 60 + minutes;
        };
        const minutesToTime = (minutes) => {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
        };
        // ✅ CURRENT TIME AND BUFFER CALCULATIONS
        const currentTimeMinutes = computed(() => {
            const now = new Date();
            return now.getHours() * 60 + now.getMinutes();
        });
        const bufferMinutes = computed(() => {
            // Standard buffer: 15min für pickup, 30min für delivery
            return props.orderType === 'pickup' ? 15 : 30;
        });
        const minAllowedMinutes = computed(() => {
            return currentTimeMinutes.value + bufferMinutes.value;
        });
        // ✅ OPERATING HOURS ANALYSIS
        const operatingHoursRanges = computed(() => {
            if (!props.operatingHours || props.operatingHours.length === 0) {
                return [];
            }
            return props.operatingHours.map(range => ({
                start: timeToMinutes(range.open),
                end: timeToMinutes(range.close),
                label: `${range.open}-${range.close}`
            }));
        });
        const earliestSlotTime = computed(() => {
            if (operatingHoursRanges.value.length === 0)
                return 6 * 60; // 06:00 default
            return Math.min(...operatingHoursRanges.value.map(r => r.start));
        });
        const latestSlotTime = computed(() => {
            if (operatingHoursRanges.value.length === 0)
                return 22 * 60; // 22:00 default
            return Math.max(...operatingHoursRanges.value.map(r => r.end));
        });
        // ✅ DYNAMIC SLIDER RANGE
        const sliderMinMinutes = computed(() => {
            // Frühster Zeitslot - 30min, aber mindestens 06:00
            const minFromSlots = earliestSlotTime.value - 30;
            return Math.max(minFromSlots, 6 * 60);
        });
        const sliderMaxMinutes = computed(() => {
            // Spätester Zeitslot + 30min, aber maximal 23:59
            const maxFromSlots = latestSlotTime.value + 30;
            return Math.min(maxFromSlots, 23 * 60 + 59);
        });
        // ✅ INTELLIGENT BACKGROUND RANGES
        const intelligentBackgroundRanges = computed(() => {
            const ranges = [];
            const currentTime = currentTimeMinutes.value;
            const minAllowed = minAllowedMinutes.value;
            // 1. Vergangene Zeit (grau) - vom Slider-Start bis jetzt
            if (currentTime > sliderMinMinutes.value) {
                ranges.push({
                    start: sliderMinMinutes.value,
                    end: Math.min(currentTime, sliderMaxMinutes.value),
                    color: '#9CA3AF', // gray-400
                    label: 'Vergangen'
                });
            }
            // 2. Pufferzeit (orange) - von jetzt bis mindestens erlaubte Zeit
            if (minAllowed > currentTime && currentTime < sliderMaxMinutes.value) {
                ranges.push({
                    start: Math.max(currentTime, sliderMinMinutes.value),
                    end: Math.min(minAllowed, sliderMaxMinutes.value),
                    color: '#F97316', // orange-500
                    label: 'Vorbereitung'
                });
            }
            // 3. Verfügbare Zeiten (grün) - Operating Hours ab mindestens erlaubter Zeit
            operatingHoursRanges.value.forEach(opHours => {
                const availableStart = Math.max(opHours.start, minAllowed);
                const availableEnd = opHours.end;
                if (availableStart < availableEnd && availableStart <= sliderMaxMinutes.value) {
                    ranges.push({
                        start: Math.max(availableStart, sliderMinMinutes.value),
                        end: Math.min(availableEnd, sliderMaxMinutes.value),
                        color: '#10B981', // green-500
                        label: `Verfügbar ${minutesToTime(opHours.start)}-${minutesToTime(opHours.end)}`
                    });
                }
            });
            return ranges;
        });
        // ✅ DYNAMIC TIME LABELS
        const timeLabels = computed(() => {
            const labels = [];
            const range = sliderMaxMinutes.value - sliderMinMinutes.value;
            const labelCount = 6; // Anzahl der Labels
            for (let i = 0; i <= labelCount; i++) {
                const position = sliderMinMinutes.value + (range * i) / labelCount;
                const roundedMinutes = Math.round(position / 60) * 60; // Runde auf volle Stunden
                labels.push({
                    minutes: roundedMinutes,
                    text: minutesToTime(roundedMinutes)
                });
            }
            return labels;
        });
        // ✅ COMPUTED VALUES
        const selectedMinutes = computed(() => {
            return props.selectedTime ? timeToMinutes(props.selectedTime) : minAllowedMinutes.value;
        });
        // ✅ TIME FORMATTING
        const formatTimeValue = (minutes) => {
            return minutesToTime(minutes);
        };
        // ✅ EVENT HANDLERS
        const updateSelectedTime = (minutes) => {
            // Constraint: Nicht vor der mindestens erlaubten Zeit
            const constrainedMinutes = Math.max(minutes, minAllowedMinutes.value);
            const timeStr = minutesToTime(constrainedMinutes);
            emit('update:selectedTime', timeStr);
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createCommentVNode(" Dynamische Zeit-Labels "),
                _createElementVNode("div", _hoisted_2, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(timeLabels.value, (label) => {
                        return (_openBlock(), _createElementBlock("span", {
                            key: label.minutes,
                            style: _normalizeStyle({ left: `${((label.minutes - sliderMinMinutes.value) / (sliderMaxMinutes.value - sliderMinMinutes.value)) * 100}%` }),
                            class: "time-label"
                        }, _toDisplayString(label.text), 5 /* TEXT, STYLE */));
                    }), 128 /* KEYED_FRAGMENT */))
                ]),
                _createCommentVNode(" SingleRangeSlider mit intelligenten Background-Ranges "),
                _createVNode(SingleRangeSlider, {
                    value: selectedMinutes.value,
                    min: sliderMinMinutes.value,
                    max: sliderMaxMinutes.value,
                    step: 5,
                    color: 'red',
                    "format-value": formatTimeValue,
                    "background-ranges": intelligentBackgroundRanges.value,
                    "background-style": 'dashed',
                    "show-min-max": true,
                    "onUpdate:value": updateSelectedTime
                }, null, 8 /* PROPS */, ["value", "min", "max", "background-ranges"]),
                _createCommentVNode(" Debug Info "),
                (showDebug.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                        _createElementVNode("p", _hoisted_4, [
                            _createTextVNode(" Debug: Operating Hours: " + _toDisplayString(_ctx.operatingHours?.length) + " entries", 1 /* TEXT */),
                            _cache[1] || (_cache[1] = _createElementVNode("br", null, null, -1 /* CACHED */)),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.operatingHours, (hours, i) => {
                                return (_openBlock(), _createElementBlock("span", { key: i }, [
                                    _createTextVNode(" Hours: " + _toDisplayString(hours.open) + "-" + _toDisplayString(hours.close), 1 /* TEXT */),
                                    _cache[0] || (_cache[0] = _createElementVNode("br", null, null, -1 /* CACHED */))
                                ]));
                            }), 128 /* KEYED_FRAGMENT */)),
                            _createTextVNode(" Range: " + _toDisplayString(minutesToTime(sliderMinMinutes.value)) + " - " + _toDisplayString(minutesToTime(sliderMaxMinutes.value)), 1 /* TEXT */),
                            _cache[2] || (_cache[2] = _createElementVNode("br", null, null, -1 /* CACHED */)),
                            _createTextVNode(" Current: " + _toDisplayString(minutesToTime(currentTimeMinutes.value)) + ", Buffer: " + _toDisplayString(bufferMinutes.value) + "min ", 1 /* TEXT */)
                        ])
                    ]))
                    : _createCommentVNode("v-if", true),
                _createCommentVNode(" Legende "),
                _cache[4] || (_cache[4] = _createStaticVNode("<div class=\"legend\" data-v-39a3bc5b><div class=\"legend-items\" data-v-39a3bc5b><div class=\"legend-item\" data-v-39a3bc5b><div class=\"legend-color past\" data-v-39a3bc5b></div><span data-v-39a3bc5b>Vergangen</span></div><div class=\"legend-item\" data-v-39a3bc5b><div class=\"legend-color buffer\" data-v-39a3bc5b></div><span data-v-39a3bc5b>Vorbereitung</span></div><div class=\"legend-item\" data-v-39a3bc5b><div class=\"legend-color available\" data-v-39a3bc5b></div><span data-v-39a3bc5b>Verfügbar</span></div></div></div>", 1)),
                _createCommentVNode(" Hinweistext "),
                _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("p", _hoisted_6, [
                        _cache[3] || (_cache[3] = _createElementVNode("i", { class: "fas fa-info-circle mr-1" }, null, -1 /* CACHED */)),
                        _createTextVNode(" Wählen Sie Ihre gewünschte Zeit für die " + _toDisplayString(_ctx.orderType === 'pickup' ? 'Abholung' : 'Lieferung'), 1 /* TEXT */)
                    ])
                ])
            ]));
        };
    }
});
